import {StyleSheet} from 'aphrodite';

import {assert, assertAndContinue} from 'Asserts';
import {NameTemplatePartType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import {assertNever} from 'util/Types';

import NameTemplatePartConfiguration from './NameTemplatePartConfiguration';

export {NameTemplatePartType} from 'graphql-client/__generated/graphqlGlobalTypes';

export enum NameTemplateType {
  ENTITY = 'ENTITY',
  CONTAINER = 'CONTAINER',
  STUDY = 'STUDY',
  PROCEDURE_RUN = 'PROCEDURE_RUN',
}

export function nameTemplateTypeToFriendlyName(
  nameTemplateType: NameTemplateType,
  nameTemplatePartConfiguration?: NameTemplatePartConfiguration
): string {
  const friendlyName = nameTemplatePartConfiguration?.getNameTemplateTypeFriendlyName?.(nameTemplateType);
  if (friendlyName != null) {
    return friendlyName;
  }
  switch (nameTemplateType) {
    case NameTemplateType.ENTITY:
      return 'entity';
    case NameTemplateType.CONTAINER:
      return 'container';
    case NameTemplateType.STUDY:
      return 'study';
    case NameTemplateType.PROCEDURE_RUN:
      assertAndContinue(
        false,
        'Name template part configuration is required to get friendly name for procedure run name template type'
      );
      return '';
    default:
      assertAndContinue(false, `Unexpected name template type ${assertNever(nameTemplateType)}`);
      return '';
  }
}

// Keep in sync with benchling/name_templates/name_template_utils.py
export const SUPPORTED_NAME_TEMPLATE_PART_TYPES_BY_NAME_TEMPLATE_TYPE: {
  [templateType in NameTemplateType]: Array<{
    sectionHeader: string | null;
    namePartTypes: Array<NameTemplatePartType>;
  }>;
} = {
  [NameTemplateType.ENTITY]: [
    {
      sectionHeader: null,
      namePartTypes: [
        NameTemplatePartType.ANTIBODY_DOMAIN,
        NameTemplatePartType.SEPARATOR,
        NameTemplatePartType.TEXT,
        NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER,
        NameTemplatePartType.CREATED_AT_YEAR,
        NameTemplatePartType.CREATED_AT_DATE,
        NameTemplatePartType.FIELD,
        NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER,
        NameTemplatePartType.HOISTED_FIELD,
        NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER,
        NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER_BASE_26,
        NameTemplatePartType.PROJECT,
        NameTemplatePartType.COMPLEX_POLYMER_COMPONENT,
      ],
    },
  ],
  [NameTemplateType.CONTAINER]: [
    {
      sectionHeader: null,
      namePartTypes: [NameTemplatePartType.SEPARATOR, NameTemplatePartType.TEXT],
    },
    {
      sectionHeader: 'Contents',
      namePartTypes: [
        NameTemplatePartType.CONTAINED_ENTITY_NAME,
        NameTemplatePartType.SAMPLE_LOT_NUMBER,
        NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER,
        NameTemplatePartType.FIELD,
        NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER,
        NameTemplatePartType.HOISTED_FIELD,
        NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER,
        NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER_BASE_26,
      ],
    },
    {
      sectionHeader: 'Container',
      namePartTypes: [
        // Container created at date & year excluded to avoid confusion with fill date/year.
        // Implementation still exists so they can be easily added back if desired.
        NameTemplatePartType.BARCODE,
        NameTemplatePartType.BARCODE_NUMBER,
        NameTemplatePartType.CONCENTRATION_VALUE,
        NameTemplatePartType.CONCENTRATION_UNITS,
        NameTemplatePartType.FILL_DATE,
        NameTemplatePartType.FILL_YEAR,
        NameTemplatePartType.PARENT_STORABLE_BARCODE,
        NameTemplatePartType.PARENT_STORABLE_NAME,
        NameTemplatePartType.POSITION,
        NameTemplatePartType.PROJECT,
        NameTemplatePartType.QUANTITY_VALUE,
        NameTemplatePartType.QUANTITY_UNITS,
        NameTemplatePartType.RESTRICTION_STATUS,
      ],
    },
  ],
  [NameTemplateType.STUDY]: [
    {
      sectionHeader: null,
      namePartTypes: [
        NameTemplatePartType.SEPARATOR,
        NameTemplatePartType.TEXT,
        NameTemplatePartType.CREATED_AT_YEAR,
        NameTemplatePartType.CREATED_AT_DATE,
        NameTemplatePartType.FIELD,
        NameTemplatePartType.PROJECT,
      ],
    },
  ],
  [NameTemplateType.PROCEDURE_RUN]: [
    {
      sectionHeader: null,
      // Some name part types are conditionally displayed based on study type. See getOptionsForNameTemplatePartType
      // within client/name-templates/GetOptionsForNameTemplatePartType.ts
      namePartTypes: [
        NameTemplatePartType.SEPARATOR,
        NameTemplatePartType.TEXT,
        NameTemplatePartType.CREATED_AT_DATE,
        NameTemplatePartType.CREATED_AT_YEAR,
        // Conditionally displayed based on study type.
        NameTemplatePartType.PROCEDURE_NAME,
        // Conditionally displayed based on study type.
        NameTemplatePartType.PROCEDURE_RUN_ID,
        NameTemplatePartType.PROJECT,
        NameTemplatePartType.STUDY_NAME,
        // Conditionally displayed based on study type.
        NameTemplatePartType.STUDY_ID,
      ],
    },
  ],
};

const NAME_TEMPLATE_PART_TYPE_TO_STRING: {[partType in NameTemplatePartType]: string | null} = {
  [NameTemplatePartType.ANTIBODY_DOMAIN]: 'Antibody domain',
  [NameTemplatePartType.FIELD]: 'Field',
  [NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER]: 'Parent lot number',
  [NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER]: 'Registry ID',
  [NameTemplatePartType.TEXT]: 'Text',
  [NameTemplatePartType.SEPARATOR]: 'Separator',
  [NameTemplatePartType.HOISTED_FIELD]: 'Hoisted field',
  [NameTemplatePartType.CREATED_AT_YEAR]: 'Creation year',
  [NameTemplatePartType.CREATED_AT_DATE]: 'Creation date',
  [NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER]: 'Registry ID number',
  [NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER_BASE_26]: 'Base-26 registry ID number',
  [NameTemplatePartType.BARCODE]: 'Barcode',
  [NameTemplatePartType.BARCODE_NUMBER]: 'Barcode number',
  [NameTemplatePartType.CONCENTRATION_UNITS]: 'Concentration units',
  [NameTemplatePartType.CONCENTRATION_VALUE]: 'Concentration value',
  [NameTemplatePartType.CONTAINED_ENTITY_NAME]: 'Entity name',
  [NameTemplatePartType.CONTAINER_CREATED_AT_DATE]: 'Container creation date',
  [NameTemplatePartType.CONTAINER_CREATED_AT_YEAR]: 'Container creation year',
  [NameTemplatePartType.FILL_DATE]: 'Fill date',
  [NameTemplatePartType.FILL_YEAR]: 'Fill year',
  [NameTemplatePartType.PARENT_STORABLE_BARCODE]: 'Parent location barcode',
  [NameTemplatePartType.PARENT_STORABLE_NAME]: 'Parent location name',
  [NameTemplatePartType.PROJECT]: 'Project',
  [NameTemplatePartType.POSITION]: 'Grid position',
  [NameTemplatePartType.RESTRICTION_STATUS]: 'Restriction status',
  [NameTemplatePartType.QUANTITY_UNITS]: 'Quantity units',
  [NameTemplatePartType.QUANTITY_VALUE]: 'Quantity value',
  [NameTemplatePartType.SAMPLE_LOT_NUMBER]: 'Sample aliquot number',
  [NameTemplatePartType.COMPLEX_POLYMER_COMPONENT]: 'Complex polymer component',
  [NameTemplatePartType.PROCEDURE_NAME]: null,
  [NameTemplatePartType.PROCEDURE_RUN_ID]: null,
  [NameTemplatePartType.STUDY_ID]: 'Study ID',
  [NameTemplatePartType.STUDY_NAME]: 'Study name',
};

/**
 * Returns the string representation of a name template part type.
 *
 * This function supports configuration overrides for name template part types that are supported by default. You can provide
 * a custom getDisplayString function in the nameTemplatePartConfiguration parameter to customize the string representation of a name template part type.
 * Example: Procedure name and procedure run ID are conditionally displayed based on study type.
 *
 * @param nameTemplatePartType - The name template part type to get the string representation of.
 * @param nameTemplatePartConfiguration - The name template part configuration override to use to get the string representation of the name template part type.
 * @returns The string representation of the name template part type.
 */
export function getNameTemplatePartTypeToString(
  nameTemplatePartType: NameTemplatePartType,
  nameTemplatePartConfiguration?: NameTemplatePartConfiguration
): string {
  // Configuration overrides are preferred and if not provided, falls back to default naming.
  const maybeFormattedNameTemplatePart =
    nameTemplatePartConfiguration?.getDisplayString?.(nameTemplatePartType);
  if (maybeFormattedNameTemplatePart != null) {
    return maybeFormattedNameTemplatePart;
  }

  const nameTemplatePartTypeString = NAME_TEMPLATE_PART_TYPE_TO_STRING[nameTemplatePartType];
  assert(
    nameTemplatePartTypeString != null,
    `Name template part type ${nameTemplatePartType} is not supported by default and no configuration was provided to handle it.`
  );
  return nameTemplatePartTypeString;
}

export type NameTemplatePart =
  | {type: NameTemplatePartType.ANTIBODY_DOMAIN; domain_type: string; specificity: string}
  | {type: NameTemplatePartType.CREATED_AT_YEAR; datetimeFormat?: string}
  | {type: NameTemplatePartType.CREATED_AT_DATE; datetimeFormat?: string}
  | {type: NameTemplatePartType.FIELD; fieldId: string; datetimeFormat?: string}
  | {
      type: NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER;
      fieldId: string;
      datetimeFormat?: string;
    }
  | {
      type: NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER;
      fieldId: string;
      datetimeFormat?: string;
    }
  | {type: NameTemplatePartType.HOISTED_FIELD}
  | {type: NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER}
  | {type: NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER_BASE_26}
  | {type: NameTemplatePartType.TEXT; text: string}
  | {type: NameTemplatePartType.SEPARATOR; text: string}
  | {type: NameTemplatePartType.PROJECT}
  // procedure run only types
  | {type: NameTemplatePartType.PROCEDURE_NAME}
  | {type: NameTemplatePartType.PROCEDURE_RUN_ID}
  | {type: NameTemplatePartType.STUDY_ID}
  | {type: NameTemplatePartType.STUDY_NAME}
  // container only types
  | {type: NameTemplatePartType.BARCODE}
  | {type: NameTemplatePartType.BARCODE_NUMBER}
  | {type: NameTemplatePartType.CONCENTRATION_UNITS}
  | {type: NameTemplatePartType.CONCENTRATION_VALUE}
  | {type: NameTemplatePartType.CONTAINED_ENTITY_NAME}
  | {type: NameTemplatePartType.CONTAINER_CREATED_AT_DATE}
  | {type: NameTemplatePartType.CONTAINER_CREATED_AT_YEAR}
  | {type: NameTemplatePartType.FILL_DATE}
  | {type: NameTemplatePartType.FILL_YEAR}
  | {type: NameTemplatePartType.PARENT_STORABLE_BARCODE}
  | {type: NameTemplatePartType.PARENT_STORABLE_NAME}
  | {type: NameTemplatePartType.POSITION}
  | {type: NameTemplatePartType.QUANTITY_VALUE}
  | {type: NameTemplatePartType.QUANTITY_UNITS}
  | {type: NameTemplatePartType.RESTRICTION_STATUS}
  | {type: NameTemplatePartType.SAMPLE_LOT_NUMBER}
  | {type: NameTemplatePartType.COMPLEX_POLYMER_COMPONENT; fieldId: string};

export interface NameTemplate {
  linkedItemId: string;
  templateType: NameTemplateType;
  nameTemplateParts: Array<NameTemplatePart>;
  shouldOrderPartsBySequence: boolean;
}

export const nameTemplateModalStyles = StyleSheet.create({
  helpText: {
    color: GRAY_DARKER,
  },
  partChips: {
    lineHeight: '32px',
    marginBottom: 10,
  },
  example: {
    ...FONT_PROXIMA_13,
    marginTop: 15,
    marginBottom: 10,
    backgroundColor: GRAY_LIGHT,
    padding: '5px 10px',
  },
  exampleLabel: {
    fontWeight: 'bold',
  },
  exampleContent: {
    color: GRAY_DARKER,
  },
});

export const nameTemplatePartChipStyles = StyleSheet.create({
  fieldName: {
    fontStyle: 'italic',
    // Some words, like "Plasmid", get cut off on the right side due to the italics, so give a
    // little more room.
    marginRight: 2,
  },
  text: {
    fontWeight: 'bold',
  },
  dragCursor: {
    cursor: 'move',
  },
});
